import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MailOpen, Coffee, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  { value: "coffee-chat", label: "Book a coffee chat" },
  { value: "everyday-tips", label: "Everyday / family safety" },
  { value: "small-biz", label: "Small business consult" },
  { value: "community", label: "Community & workshops" },
  { value: "just-hi", label: "Just saying hi" },
];

export default function Connect() {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [nlHp, setNlHp] = useState("");
  const [form, setForm] = useState({ name: "", email: "", topic: "coffee-chat", message: "", website: "" });
  const [contactLoading, setContactLoading] = useState(false);

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email: newsletterEmail, website: nlHp });
      toast.success(data.message);
      setNewsletterEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Hmm, that didn't work. Try again?");
    } finally {
      setNewsletterLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message);
      setForm({ name: "", email: "", topic: "coffee-chat", message: "", website: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Hmm, that didn't work. Try again?");
    } finally {
      setContactLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-input bg-card px-4 py-3.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-shadow duration-200";

  return (
    <section id="contact" className="relative overflow-hidden py-24 lg:py-32 bg-secondary/60 border-t border-border scroll-mt-20" data-testid="connect-section">
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <span className="absolute -left-20 bottom-0 font-display font-semibold text-[24rem] leading-none text-[#8FBEE3]/25 -rotate-[8deg] hidden md:block">y</span>
        <span className="absolute right-10 -top-20 font-display font-semibold text-[18rem] leading-none text-primary/[0.07] rotate-[10deg] hidden lg:block">c</span>
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-16"
        >
          <p className="font-hand text-2xl text-primary mb-3">say hello</p>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="connect-heading">
            Two easy ways to <span className="text-primary">stay safer this week.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 bg-foreground text-background rounded-3xl p-8 lg:p-12 flex flex-col"
            data-testid="newsletter-card"
          >
            <MailOpen className="w-8 h-8 text-accent mb-6" strokeWidth={1.8} />
            <h3 className="font-display font-semibold text-2xl lg:text-3xl tracking-tight mb-4">
              The Weekly YYC Tip
            </h3>
            <p className="text-background/70 text-sm md:text-base leading-relaxed mb-10 flex-1">
              One email a week. One two-minute read. One thing you can do tonight to make a scammer's
              day worse. No spam — ironically, we hate it too.
            </p>
            <form onSubmit={submitNewsletter} className="flex flex-col gap-3" data-testid="newsletter-form">
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={nlHp}
                onChange={(e) => setNlHp(e.target.value)}
                className="absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0"
                data-testid="newsletter-honeypot"
              />
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="you@example.ca"
                data-testid="newsletter-email-input"
                className="w-full rounded-xl border border-background/20 bg-background/10 px-4 py-3.5 text-sm text-background placeholder:text-background/50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-foreground transition-shadow duration-200"
              />
              <button
                type="submit"
                disabled={newsletterLoading}
                data-testid="newsletter-submit-button"
                className="bg-accent text-accent-foreground font-display font-medium px-8 py-4 rounded-full hover:scale-[1.02] transition-transform duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {newsletterLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Sign me up
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-3 bg-card border border-border rounded-3xl p-8 lg:p-12"
            data-testid="contact-card"
          >
            <Coffee className="w-8 h-8 text-primary mb-6" strokeWidth={1.8} />
            <h3 className="font-display font-semibold text-2xl lg:text-3xl tracking-tight mb-4">
              Book a Coffee Chat
            </h3>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-10">
              Worried about a weird text? Want your small business locked down? First chat is always
              free — our treat, you pick the coffee shop.
            </p>
            <form onSubmit={submitContact} className="grid sm:grid-cols-2 gap-4" data-testid="contact-form">
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0"
                data-testid="contact-honeypot"
              />
              <div>
                <label htmlFor="contact-name" className="sr-only">Your name</label>
                <input
                  id="contact-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="contact-name-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="sr-only">Your email</label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email"
                  data-testid="contact-email-input"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-topic" className="sr-only">Topic</label>
                <select
                  id="contact-topic"
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  data-testid="contact-topic-select"
                  className={inputCls}
                >
                  {TOPICS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="sr-only">How can we help?</label>
                <textarea
                  id="contact-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help?"
                  data-testid="contact-message-input"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={contactLoading}
                data-testid="contact-submit-button"
                className="sm:col-span-2 bg-primary text-primary-foreground font-display font-medium px-8 py-4 rounded-full hover:scale-[1.02] transition-transform duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {contactLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Send it over
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
